import React, { useState, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import useTranslate from '../../Hooks/useTranslate';
import { Typography, Paper, Collapse, Box } from '@mui/material';
import { SchemaRenderer } from './SchemaRenderer';
import { FlexBox, ColumnFlexBox } from '../FlexBox';
import { makeGenericFieldAtom } from '../../store/genericFormStore';
import CollapseToggle from '../Button/CollapseToggle';
import Switch from '../Inputs/FormSwitch';

const SchemaPaper = function SchemaPaper({
  name,
  description,
  active,
  dynamicPath,
  uiSchemaConfiguration,
  onToggle,
  sectionId,
  waveFilterId,
  showHeader = true,
  showToggle = true,
}) {
  const translate = useTranslate();

  const [collapsed, setCollapsed] = useState(false);

  // The active state lives in the generic form payload at `${dynamicPath}.active`
  // so the toggle persists into the form.
  const activeAtom = useMemo(
    () => makeGenericFieldAtom(`${dynamicPath}.active`),
    [dynamicPath]
  );
  const [storedActive, setIsActive] = useAtom(activeAtom);

  // Without the toggle the schema is always live and `active` is left out of
  // the payload entirely.
  const isActive = showToggle ? storedActive : true;

  // Seed the store with the initial (API) value if nothing has been set yet.
  // `isInit` keeps this silent so it doesn't mark the form as changed.
  useEffect(() => {
    if (showToggle && storedActive === undefined) {
      setIsActive(!!active, { isInit: true });
    }
  }, [showToggle, storedActive, active, setIsActive]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const handleToggle = () => {
    const nextActive = !isActive;

    // Persist server-side first; only flip the form state once the API confirms.
    if (onToggle) {
      onToggle(sectionId, waveFilterId, () => {
        setIsActive(nextActive);
      });
    } else {
      setIsActive(nextActive);
    }
  };

  const isInactive = !isActive;
  const hasHeader = showHeader || showToggle;

  return (
    // Without a header there is nothing for the card to be about, and the filter panels render it
    // inside a dialog that already draws its own surface — so it is flat there rather than a
    // bordered box inside a bordered box.
    <Paper
      variant={hasHeader ? 'outlined' : 'elevation'}
      elevation={0}
      square={!hasHeader}
      sx={{
        padding: hasHeader ? '16px 24px' : 0,
        backgroundColor: hasHeader ? undefined : 'transparent',
        width: '100%',
        opacity: isInactive ? 0.5 : 1,
        transition: 'opacity 0.2s ease, background-color 0.2s ease',
      }}
    >
      {hasHeader && (
        <FlexBox alignItems="center" gap="16px" marginBottom="8px">
          <ColumnFlexBox flexGrow={1}>
            {showHeader && name && (
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {translate(name)}
              </Typography>
            )}
            {showHeader && description && (
              <Typography variant="subtitle1" color="text.secondary">
                {translate(description)}
              </Typography>
            )}
          </ColumnFlexBox>
          {showToggle && (
            <>
              <Switch
                checked={!!isActive}
                onChange={handleToggle}
                label={isActive ? translate('ACTIVE') : translate('INACTIVE')}
                labelPlacement="start"
                slotProps={{
                  typography: {
                    sx: {
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      color: isInactive ? 'text.disabled' : 'primary.main',
                      fontSize: '0.9rem',
                    },
                  },
                }}
              />
              <CollapseToggle
                collapsed={collapsed}
                onToggle={toggleCollapsed}
                disabled={isInactive}
              />
            </>
          )}
        </FlexBox>
      )}
      <Collapse in={!collapsed}>
        <Box sx={{ pointerEvents: isInactive ? 'none' : 'auto' }}>
          <SchemaRenderer
            key={dynamicPath}
            basePath={dynamicPath}
            fields={uiSchemaConfiguration ?? []}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SchemaPaper;
